<template>
  <div class="pt-20 p-4 flex flex-col items-center bg-light-bg dark:bg-dark-bg min-h-screen text-light-text dark:text-dark-text">
    <AppSidebar />

    <div class="w-full max-w-6xl bg-white dark:bg-dark-box-bg rounded-lg shadow-md p-6 mt-6">


      <button
          @click="goToOrders"
          class="mb-4 text-teal-700 dark:text-teal-400 hover:underline flex items-center"
      >
        &larr; {{ returnToText }}
      </button>

        <div v-if="order">
              <div class="relative mb-6">
                <h2 class="text-2xl font-bold">Szczegóły Zlecenia</h2>
                <span
                    :class="getStatusBadgeClass(order?.orderStatus)"
                    class="absolute top-0 right-0 px-3 py-1 text-sm font-medium rounded-full"
                >
          {{ getOrderStatusInPolish(order?.orderStatus) }}
          </span>
        </div>



        <!-- Order General Information -->
        <div class="mb-6">
          <h3 class="text-lg font-semibold mb-2">
            Numer Zlecenia: {{ order.mpk }}
          </h3>
          <p class="text-lg">
            <strong>Unikalne ID:</strong> {{ order.uniq_id }}
          </p>
          <p class="text-lg">
            <strong>Połączone Zlecenie:</strong> {{ order.orderlink || 'Brak połączonego zlecenia' }}
          </p>
          <p class="text-lg">
            <strong>Data:</strong> {{ new Date(order.date).toLocaleDateString() }}
          </p>
          <p class="text-lg">
            <strong>Nie liczymy dojazdu:</strong> {{ order.direction ? 'Tak' : 'Nie' }}
          </p>
          <p class="text-lg">
            <strong>Nie liczymy powrotu:</strong> {{ order.return ? 'Tak' : 'Nie' }}
          </p>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <!-- Grid Layout for Company and Passenger Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Company Information -->
          <div>
            <h4 class="text-lg font-semibold mb-4">Informacje o Firmie</h4>
            <p><strong>Nazwa:</strong> {{ order.company.companyName }}</p>
            <p><strong>NIP:</strong> {{ order.company.companyNip }}</p>
            <p><strong>Adres:</strong> {{ order.company.companyAddress }}</p>
            <p><strong>Telefon:</strong> {{ order.company.phone }}</p>
          </div>

          <!-- Passenger Information -->
          <div>
            <h4 class="text-lg font-semibold mb-4">Informacje o Pasażerze</h4>
            <div
                v-for="(passenger, index) in order.passenger"
                :key="index"
                class="mb-4"
            >
              <p><strong>Imię i nazwisko:</strong> {{ passenger.firstName }} {{ passenger.lastName }}</p>
              <p><strong>Odbiór:</strong> {{ passenger.pickupAddress }}</p>
              <p><strong>Dostawa:</strong> {{ passenger.dropoffAddress }}</p>
              <p><strong>Godzina odbioru:</strong> {{ passenger.pickupTime }}</p>
              <p><strong>Wspólny adres:</strong> {{ passenger.sharedDropoff ? 'Tak' : 'Nie' }}</p>
              <div v-if="passenger.closestLocation">
                <!-- <p><strong>Najbliższa lokalizacja:</strong> {{ passenger.closestLocation.latitude }}, {{ passenger.closestLocation.longitude }}</p> -->
                <div v-if="passenger.railwayStations && passenger.railwayStations.length">
                  <p><strong>Najbliższe stacje kolejowe podczas odstawienia pasażera (w promieniu 1km):</strong></p>
                  <ul class="list-disc pl-5">
                    <li v-for="station in passenger.railwayStations" :key="station.place_id">
                      {{ station.display_name }} ({{ station.distance ? station.distance.toFixed(2) : 'N/A' }} km)
                    </li>
                  </ul>
                </div>
                <p v-else-if="passenger.railwayStationsLoading">Ładowanie stacji kolejowych...</p>
                <p v-else-if="passenger.railwayStationsError" class="text-red-500">
                  {{ passenger.railwayStationsError }}
                </p>
                <p v-else>Brak stacji kolejowych w promieniu 1km.</p>
              </div>
            </div>
          </div>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <!-- Driver Information -->
        <div class="mb-6">
          <h4 class="text-lg font-semibold mb-4">Informacje o Kierowcy</h4>
          <div v-if="order.driver">
            <p><strong>Imię i nazwisko:</strong> {{ order.driver.firstName }} {{ order.driver.lastName }}</p>
            <p><strong>Pojazd:</strong> {{ order.driver.car }}</p>
            <p><strong>Rok produkcji:</strong> {{ order.driver.carYear }}</p>
            <p><strong>Miasto:</strong> {{ order.driver.city }}</p>
            <p><strong>Adres:</strong> {{ order.driver.homeAddress }}</p>
            <p><strong>Status:</strong> {{ order.driver.driverStatus }}</p>
          </div>
          <p v-else class="text-sm">Brak przypisanego kierowcy.</p>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <!-- Notes -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 class="text-lg font-semibold mb-4">Notatki Kierowcy</h4>
            <p>{{ order.notesDriver || "Brak notatek" }}</p>
          </div>
          <div>
            <h4 class="text-lg font-semibold mb-4">Notatki Dyspozytora</h4>
            <p>{{ order.notesDispatcher || "Brak notatek" }}</p>
          </div>
        </div>

        <hr class="my-6 border-light-border dark:border-dark-border">

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
  <div>
    <h4 class="text-lg font-semibold mb-4">Szczegóły Trasy</h4>
    <p v-if="order.startDateTime"> Kierowca wystartował o: {{ new Date(order.startDateTime).toLocaleString() }}</p>
    <p v-else>Kierowca jeszcze nie wystartował.</p>

    <p v-if="order.endDateTime"> Kierowca zakończył o: {{ new Date(order.endDateTime).toLocaleString() }}</p>
    <p v-else>Kierowca jeszcze nie zakończył.</p>

    <p v-if="elapsedTime && elapsedTime > 0"><b>Czas trwania trasy:</b> {{ elapsedTime }} minut ({{ (elapsedTime / 60).toFixed(2) }} godzin/y)</p>

    <div v-if="order.distanceInKm" class="mt-4">
      <p><b>Ilość km trasy według map (Od punktu odbioru do punktu odstawy): </b>{{ order.distanceInKm }} Km</p>
    </div>
    <div v-if="order.accessKilometers" class="mt-4">
      <p>
        <b>Ilość km dojazdu do pierwszego pasażera: </b>{{ (order.accessKilometers).toFixed(2) }} Km <span v-if="order.direction" class="text-red-500"> (Nie liczymy dojazdu w tym przypadku)</span>
      </p>
    </div>
        <div v-if="order.returnKilometers" class="mt-4">
      <p>
        <b>Ilość km powrotu: </b>{{ (order.returnKilometers).toFixed(2) }} Km <span v-if="order.return" class="text-red-500"> (Nie liczymy powrotu w tym przypadku)</span>
      </p>
    </div>
  </div>

  <div>
    <h4 class="text-lg font-semibold mb-4">Odbiór Pasażerów</h4>
    <div v-for="(passenger) in order.passenger" :key="passenger.passengerId">
      <div v-if="passenger" class="my-2">
        <p><b>{{ passenger.firstName }} {{ passenger.lastName }}: </b></p>

        <p v-if="passenger.waitingStartTime">
          Oczekiwanie: {{ new Date(passenger.waitingStartTime).toLocaleString() }}
        </p>
        <p v-else>Kierowca jeszcze nie oczekiwał na tego pasażera.</p>

        <p v-if="passenger.waitingEndTime">
          Koniec Oczekiwania: {{ new Date(passenger.waitingEndTime).toLocaleString() }}
        </p>
        <p v-else>Kierowca jeszcze nie zakończył czekać na tego pasażera.</p>

        <p v-if="passenger.waitingStartTime && passenger.waitingEndTime">
          Czas oczekiwania:
          {{ calculateWaitingTime(passenger.waitingStartTime, passenger.waitingEndTime).total }} minut
          <span v-if="calculateWaitingTime(passenger.waitingStartTime, passenger.waitingEndTime).afterFree > 0">
            ({{ calculateWaitingTime(passenger.waitingStartTime, passenger.waitingEndTime).afterFree }} minut po 60 min)
          </span>
        </p>

        <p v-if="passenger.waitingDroppedOffTime">
          Czas oczekiwania wysadzenia o: {{ new Date(passenger.waitingDroppedOffTime).toLocaleString() }}
        </p>
        <p v-if="passenger.droppedOffTime">
          Wysadzony o: {{ new Date(passenger.droppedOffTime).toLocaleString() }}
        </p>
        <p v-else>Pasażer jeszcze nie został wysadzony.</p>

        <p>

        </p>
      </div>
    </div>

    <div v-if="totalWaitingTime() > 0" class="mt-4">
      <p><b>Całkowity czas oczekiwania na pasażerów: </b>{{ totalWaitingTime() }} minut</p>
    </div>
  </div>
</div>


<hr class="my-6 border-light-border dark:border-dark-border">

<!-- Map Section -->
<div class="mb-6">
  <h4 class="text-lg font-semibold mb-4">Mapa Trasy</h4>

  <p class="text-red-500 mb-4" v-if="order.orderStatus == 'W_TRAKCIE'">Mapa nie jest odświeżana ponieważ status zlecenia to: W trakcie. (Aby ułatwić zlokalizowanie kierowcy)</p>

  <FleetMap :order="order" :totalWaitingTime="totalWaitingTime()" />

  <div v-if="this.errorMessage" class="text-red-500">
    {{ this.errorMessage }} 
  </div>
</div>

<hr class="my-6 border-light-border dark:border-dark-border">

<!-- Order Logs -->
<div>
  <h4 class="text-lg font-semibold mb-4">Logi Zlecenia</h4>
  <div v-if="paginatedOrderLogs && paginatedOrderLogs.length">
    <div
      v-for="log in paginatedOrderLogs"
      :key="log.id"
      class="p-4 bg-light-row-odd dark:bg-dark-row-odd rounded-lg mb-6 shadow-md"
    >
      <div class="space-y-2 mb-4">
        <p class="text-sm font-medium text-light-text dark:text-dark-text">
          <span class="font-bold">ID Logu:</span> {{ log.id }}
        </p>
        <p class="text-sm font-medium text-light-text dark:text-dark-text">
          <span class="font-bold">Zmiany przez:</span> {{ log.changedBy }}
        </p>
        <p class="text-sm font-medium text-light-text dark:text-dark-text">
          <span class="font-bold">Data:</span> {{ new Date(log.changeDate).toLocaleString() }}
        </p>
      </div>

      <div class="border-t border-light-border dark:border-dark-border pt-4">
        <p class="text-sm font-semibold text-light-text dark:text-dark-text mb-4">
          <strong>Opis:</strong>
        </p>
        <pre class="mb-4 bg-light-row-even dark:bg-dark-row-even p-4 rounded-md w-full overflow-x-auto whitespace-pre-wrap text-sm font-mono">{{ log.changeDescription }}</pre>
      </div>
    </div>
  </div>
  <p v-else class="text-sm text-center text-gray-500 dark:text-gray-400">Brak logów dla tego zlecenia.</p>
</div>

<!-- Paginacja -->
<div class="mt-4 flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
  <div class="text-center sm:text-left text-sm text-light-text dark:text-gray-400">
    Wyświetlono {{ (currentPage - 1) * itemsPerPage + paginatedOrderLogs.length }} z {{ orderLog.length }} logów
  </div>

  <div class="flex flex-wrap justify-center sm:justify-end items-center gap-2">
    <button v-if="currentPage > 1"
            @click="goToPage(1)"
            class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
      Pierwsza strona
    </button>
    <button v-if="currentPage > 1"
            @click="prevPage"
            class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
      &laquo; Poprzednia
    </button>
    <button v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="[currentPage === page ? 'bg-teal-700 text-white' : 'bg-gray-200 dark:bg-dark-box-bg text-gray-700 dark:text-dark-text']"
            class="px-3 py-1 text-sm font-medium rounded-md transition-colors hover:bg-teal-700 hover:text-white dark:hover:bg-teal-600 dark:hover:text-white">
      {{ page }}
    </button>
    <button v-if="currentPage < totalPages"
            @click="nextPage"
            class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
      Następna &raquo;
    </button>
    <button v-if="currentPage < totalPages"
            @click="goToPage(totalPages)"
            class="px-3 py-1 text-sm font-medium bg-light-table-header rounded-md dark:bg-dark-table-header dark:text-dark-text">
      Ostatnia strona
    </button>
  </div>
</div>



      </div>

      <!-- Loading State -->
      <div v-else-if="loading" class="text-gray-500">
        Ładowanie szczegółów zlecenia...
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="text-red-500">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>


<script>
import { useAuthStore } from '@/stores/authStore';
import orderService from '@/services/orderService';
import orderLogService from '@/services/orderLogService';
import AppSidebar from '@/components/AppSidebar.vue';
import { OrderStatusMap } from '../utils/orderStatusMap';
import FleetMap from '../map/FleetMap.vue';
import driverLocationService from '@/services/driverLocationService';

export default {
  name: 'OrderProfile',
  components: { AppSidebar, FleetMap },
  data() {
    return {
      order: null,
      orderLog: [],
      loading: true,
      errorMessage: '',
      currentPage: 1,            // Aktualna strona
      itemsPerPage: 3,           // Liczba logów na stronę
      visiblePages: [],          // Tablica stron do wyświetlenia
      totalPages: 1,             // Całkowita liczba stron
    };
  },
  setup() {
    const authStore = useAuthStore();
    authStore.checkExpiration();
    return { user: authStore.user };
  },
  async mounted() {
    await this.fetchOrder();
    await this.fetchOrderLogsByOrderId();
    this.calculateTotalPages();
    if (this.order?.passenger) {
      for (let passenger of this.order.passenger) {
        if (passenger.droppedOffTime) {
          await this.fetchClosestLocation(passenger);
          if (passenger.closestLocation) {
            await this.fetchNearbyRailwayStations(passenger);
          }
        }
      }
    }
  },
  computed: {
    paginatedOrderLogs() {
      if (!Array.isArray(this.orderLog)) {
        return []; // Jeśli orderLog nie jest tablicą, zwróć pustą tablicę
      }

      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.orderLog.slice(start, end);
    },
    returnToText() {
      const returnTo = this.$route.query.returnTo; // Pobranie wartości returnTo
      switch (returnTo) {
        case 'DriverProfileAdmin': 
        case 'DriverProfileDispatcher':
          return 'Wróć do Profilu Kierowcy';
        case 'PassengerProfileAdmin':
        case 'PassengerProfileDispatcher':
          return 'Wróć do Profilu Pasażera';
        case 'CompanyProfileAdmin':
        case 'CompanyProfileDispatcher':
          return 'Wróć do Profilu Firmy';
        case 'PayoffProfileAdmin':
        case 'PayoffProfileDispatcher':
          return 'Wróć do Profilu Rozliczeń';
        default:
          return 'Wróć do Zakładki Zlecenia';
      }
    },
    elapsedTime() {
      const start = this.order.startDateTime ? new Date(this.order.startDateTime) : null;
      const end = this.order.endDateTime ? new Date(this.order.endDateTime) : null;

      // Sprawdzamy, czy obie daty są poprawne
      if (start && end && !isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const diff = (end - start) / (1000 * 60); // Różnica w minutach
        return Math.round(diff); // Zaokrąglamy do pełnych minut
      }
      return 0; // Jeśli daty są niepoprawne, zwracamy 0 minut
    },
  },
  methods: {

    getStatusBadgeClass(orderStatus) {
      switch (orderStatus) {
        case 'DO_OBSLUGI':
          return 'bg-violet-500 text-white';
        case 'W_TRAKCIE':
          return 'bg-yellow-400 text-white';
        case 'ZAKONCZONY':
          return 'bg-red-500 text-white';
        case 'OCZEKUJACY':
          return 'bg-blue-500 text-white';
        case 'NOWY':
          return 'bg-green-500 text-white';
        default:
          return 'bg-gray-400 text-white';
      }
    },

    calculateWaitingTime(startTime, endTime) {
      const diffMs = new Date(endTime) - new Date(startTime);
      const diffMinutes = Math.floor(diffMs / (1000 * 60));

      const freeMinutes = 60;
      const waitingTimeAfterFree = diffMinutes > freeMinutes ? diffMinutes - freeMinutes : 0;

      return {
        total: diffMinutes,
        afterFree: waitingTimeAfterFree
      };
    },
    // W sekcji methods:
    totalWaitingTime() {
      if (!this.order || !Array.isArray(this.order.passenger)) return 0;

      return this.order.passenger.reduce((total, passenger) => {
        if (passenger.waitingStartTime && passenger.waitingEndTime) {
          // Pobieramy OBIE wartości
          const waitingTimes = this.calculateWaitingTime(passenger.waitingStartTime, passenger.waitingEndTime);
          // Sumujemy TYLKO czas po odliczeniu darmowych minut
          return total + waitingTimes.afterFree;
          // Jeśli chcesz sumować CAŁKOWITY czas oczekiwania (łącznie z darmowymi 60 min), użyj:
          // return total + waitingTimes.total;
        }
        return total;
      }, 0);
    },


    calculateTotalPages() {
      this.totalPages = Math.ceil(this.orderLog.length / this.itemsPerPage) || 1;
      this.updateVisiblePages();
    },

    // Uaktualnianie widocznych stron
    updateVisiblePages() {
      const range = 5; // Liczba widocznych stron
      const start = Math.max(1, this.currentPage - Math.floor(range / 2));
      const end = Math.min(start + range - 1, this.totalPages);

      this.visiblePages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    // Przejście do konkretnej strony
    goToPage(page) {
      this.currentPage = page;
      this.updateVisiblePages();
    },

    // Przejście do następnej strony
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.updateVisiblePages();
      }
    },

    // Przejście do poprzedniej strony
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.updateVisiblePages();
      }
    },
    async fetchNearbyRailwayStations(passenger) {
      passenger.railwayStationsLoading = true;
      passenger.railwayStationsError = null;
      try {
        const { latitude, longitude } = passenger.closestLocation;
        const delta = 0.009;
        const viewbox = `${longitude - delta},${latitude + delta},${longitude + delta},${latitude - delta}`;
        const url = `https://nominatim.openstreetmap.org/search?q=railway+station&format=json&viewbox=${viewbox}&bounded=1&limit=10`;
        
        // const proxyUrl = 'https://cors-anywhere.herokuapp.com/';
        const response = await fetch(url, {
          headers: {
            'User-Agent': 'GrokOrderProfile/1.0 (contact: your-email@example.com)'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const stations = await response.json();
        const stationsWithDistance = stations.map(station => ({
          ...station,
          distance: this.calculateDistance(latitude, longitude, parseFloat(station.lat), parseFloat(station.lon))
        })).filter(station => station.distance <= 1);

        passenger.railwayStations = stationsWithDistance; // Direct assignment
      } catch (error) {
        passenger.railwayStationsError = 'Nie udało się pobrać danych o stacjach kolejowych.';
        console.error('Błąd podczas pobierania stacji kolejowych:', error);
      } finally {
        passenger.railwayStationsLoading = false;
      }
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      // Haversine formula to calculate distance between two points in kilometers
      const R = 6371; // Earth's radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },
    async fetchClosestLocation(passenger) {
      try {
        const response = await driverLocationService.getClosestDriverLocation(this.order.id, passenger.droppedOffTime);
        passenger.closestLocation = response.data;
      } catch (error) {
        this.errorMessage = `Nie udało się pobrać lokalizacji Kierowcy dla pasażera ${passenger.firstName} ${passenger.lastName}. (Prawdopodobnie brak danych lokalizacji)`;
        console.error(`Błąd podczas pobierania lokalizacji Kierowcy dla pasażera ${passenger.passengerId}:`, error);
      }
    },
    async fetchOrder() {
      const orderId = this.$route.params.id; // Pobieramy id z parametrów URL
      try {
        const response = await orderService.getOrderById(orderId);
        this.order = response.data;
        console.log(this.order);
        this.loading = false;
      } catch (error) {
        this.errorMessage = 'Nie udało się pobrać szczegółów zlecenia. Spróbuj ponownie.';
        this.loading = false;
      }
    },
    async fetchOrderLogsByOrderId() {
      const orderId = this.$route.params.id; // Pobieramy id z parametrów URL
      try {
        const response = await orderLogService.getOrderLogsByOrderId(orderId);
        this.orderLog = response.data;
        this.loading = false;
      } catch (error) {
        this.errorMessage = 'Nie udało się pobrać szczegółów zlecenia. Spróbuj ponownie.';
        this.loading = false;
      }
    },
    getOrderStatusInPolish(orderStatus) {
      return OrderStatusMap[orderStatus] || 'Nieznany status';
    },
    goToOrders() {
      const returnTo = this.$route.query.returnTo; // Pobranie nazwy trasy powrotnej
      const currentPage = this.$route.query.currentPage || '1'; // Domyślnie strona 1
      let id;

      // Ustalanie wartości id na podstawie kontekstu
      if (returnTo === 'PayoffProfileAdmin' || returnTo === 'PayoffProfileDispatcher') {
        id = this.$route.query.payoffId; // Użyj payoffId dla PayoffProfileAdmin
      } else {
        id = this.$route.query.driverId || this.$route.query.passengerId || this.$route.query.companyId;
      }

      if (returnTo) {
        if (id) {
          this.$router.push({
            name: returnTo,
            params: { id: id },
            query: { currentPage: currentPage }
          });
        } else {
          this.$router.push({
            name: returnTo,
            query: { currentPage: currentPage }
          });
        }
      } else {
        // Domyślne zachowanie, jeśli brak informacji o poprzedniej trasie
        const targetRoute = this.user.role === 'ROLE_ADMIN' ? 'OrdersPageAdmin' : 'OrdersPageDispatcher';
        this.$router.push({
          name: targetRoute,
          query: { currentPage: currentPage }
        });
      }
    }
  }
};
</script>